import { useState, useRef, useContext, useEffect } from "react";
import { Box, Button, Grid, Link } from "@mui/material";

// components
import SamplesFilter from "./SamplesFilter";
import SampleMenuList from "./SampleMenuList";
import MenuContext from "../../store/menu-context";
import Glyphosate from "./Glyphosate";
import CircleSvg from "../CircleSvg";
import SamplingRegion from "./SamplingRegion";
import LineSvg from "../LineSvg";
import ImportControl from "./ImportControl";
import DottedCircleSvg from "../DottedCircleSvg";
import AnimalProductsFish from "./AnimalProductsFish";
import AnimalProducts from "./AnimalProducts";
import PesticideResiduesResults from "./PesticideResiduesResults";
import ProcessedFoodProducts from "./ProcessedFoodProducts";
import OrganicFoodProducts from "./OrganicFoodProducts";
import BabyFood from "./BabyFood";

// maps
import CountryOfOriginMap from "../Maps/CountryOfOriginMap";
import mapCSV from "../../Data/MANCP/Geospatial/MANCP-Country-of-origin.csv";
import mapJSON from "../../Data/JSON/World.json";

import classes from "../../Style/ReportingCountry.scss";

const CountryOfOrigin = () => {
  const anchorOne = useRef(null);
  const anchorTwo = useRef(null);
  const anchorThree = useRef(null);

  const filters = [
    "Number of samples",
    "Difference in samples from past year (%)",
    "Rate of samples below LOQ",
    "Rate of samples between LOQ and MRL",
    "Rate of quantification",
    "Rate of non-compliant samples",
    "Rate of samples above legal limit",
  ];

  const [samplesFilter, setSamplesFilter] = useState(filters[0]);

  const handleFilterChange = (evt) => {
    setSamplesFilter(evt);
  };

  const ctx = useContext(MenuContext);
  if (!ctx.scrollEnabled && anchorOne.current !== null) {
    if (ctx.index === 0) {
      window.scrollTo({
        top: anchorOne.current.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    } else if (ctx.index === 1) {
      window.scrollTo({
        top: anchorTwo.current.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    } else if (ctx.index === 2) {
      window.scrollTo({
        top: anchorThree.current.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    }
  }

  const handleScroll = (e) => {
    const scrollTop = e.srcElement.firstElementChild.scrollTop;
    if (
      anchorOne.current !== null &&
      scrollTop >= 0 &&
      scrollTop < anchorOne.current.offsetTop
    ) {
      ctx.updateContext(0, true);
    } else if (
      anchorTwo.current !== null &&
      scrollTop >= anchorTwo.current.offsetTop - 100 &&
      scrollTop < anchorThree.current.offsetTop - 100
    ) {
      ctx.updateContext(1, true);
    } else if (
      anchorThree.current !== null &&
      scrollTop >= anchorThree.current.offsetTop - 100
    ) {
      ctx.updateContext(2, true);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", (e) => handleScroll(e));
    return () => {
      window.removeEventListener("scroll", (e) => handleScroll(e));
    };
  }, []);

  return (
    <Grid container direction="column" className="outer-wrapper-reporting">
      <Grid container direction="column" className="reportingCountryWrapper">
        {/* First row */}
        <Grid
          ref={anchorOne}
          item
          container
          direction={{ xs: "column", sm: "column", md: "row" }}
          className="reportingCountryFindings"
        >
          <Grid item xs p={3} className="one-one">
            <Box display="flex" flexDirection="column">
              <div className="lineSvg">
                <LineSvg />
              </div>
              <span>MANCP RESULTS</span>
              <h3>Country of origin</h3>
            </Box>
            <Box
              sx={{
                display: { xs: "none", sm: "none", md: "block" },
                marginTop: { xs: 0, sm: 0, md: "10rem" },
              }}
            >
              <SamplesFilter
                title="Number of samples"
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </Box>
            <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
              <span style={{ fontSize: "14px" }}>Filter by</span>
              <SampleMenuList
                title="Number of samples"
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </Box>
          </Grid>
          <Grid item xs p={3} className="one-two">
            <CountryOfOriginMap
              csv={mapCSV}
              samplesFilter={samplesFilter}
              json={mapJSON}
            />
          </Grid>
        </Grid>
        {/* First row */}

        {/* Second row */}
        <Grid>
          <div className="circle-svg">
            <CircleSvg />
          </div>
          <SamplingRegion />
        </Grid>
        {/* Second row */}

        {/* Third row */}
        <Grid ref={anchorTwo}>
          <PesticideResiduesResults />
        </Grid>
        {/* Third row */}

        {/* Fourth row */}
        <Grid>
          <Glyphosate />
        </Grid>
        {/* Fourth row */}

        {/* Fifth row */}
        <Grid>
          <ImportControl />
        </Grid>
        {/* Fifth row */}

        {/* Sixth row */}
        <Grid ref={anchorThree}>
          <ProcessedFoodProducts />
        </Grid>
        {/* Sixth row */}

        {/* Seventh row */}
        <Grid>
          <OrganicFoodProducts />
        </Grid>
        {/* Seventh row */}

        {/* Eighth row */}
        <Grid>
          <BabyFood />
        </Grid>
        {/* Eighth row */}

        {/* Ninth row */}
        <Grid>
          <AnimalProducts />
        </Grid>
        {/* Ninth row */}

        {/* Tenth row */}
        {/* <Grid>
          <AnimalProductsFish />
        </Grid> */}
        {/* Tenth row */}

        {/* Eleventh row */}
        <Grid
          item
          container
          direction="column"
          alignContent="center"
          className="footerWrapper"
        >
          <div className="dottedCircle">
            <DottedCircleSvg />
          </div>
          <Grid item xs className="footer">
            <Box className="nine-one-inner">
              <p>
                <b>Read more about the 2023 results</b>
              </p>
              <Button
                className="footerButton"
                variant="outlined"
                component={Link}
                target="_blank"
                href="https://efsa.onlinelibrary.wiley.com/doi/full/10.2903/j.efsa.2025.9398"
                rel="noreferrer"
              >
                SCIENTIFIC REPORT
              </Button>
            </Box>
          </Grid>
        </Grid>
        {/* Eleventh row */}
      </Grid>
    </Grid>
  );
};

export default CountryOfOrigin;
